/**
 * Cart Drawer Web Component
 * 
 * A sliding drawer component that animates from left to right
 * Can be used for cart, menus, or other side panels
 * 
 * Usage:
 * <cart-drawer>
 *   <div slot="content" data-drawer-content>
 *     <!-- drawer content -->
 *   </div>
 * </cart-drawer>
 * 
 * To open: cartDrawer.open()
 * To close: cartDrawer.close()
 * 
 * Attributes:
 * - animation-duration: Duration in milliseconds (default: 600)
 * - position: 'left' or 'right' (default: 'left')
 */
class CartDrawer extends HTMLElement {
  static openInstances = 0;
  constructor() {
    super();

    // Initialize state only
    this.animationDuration = parseInt(this.getAttribute('animation-duration')) || 600;
    this.position = this.getAttribute('position') || 'left';
    this.isOpen = false;

    // Bind event handlers once in constructor
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
  }

  connectedCallback() {
    this.content = this.querySelector('[data-drawer-content]');
    this.closeButton = this.querySelector('[data-drawer-close]');

    if (!this.content) {
      console.warn('CartDrawer: Missing required [data-drawer-content] element');
      return;
    }

    // Ensure drawer starts hidden before initialization
    this.setAttribute('aria-hidden', 'true');
    
    // Use requestAnimationFrame to ensure CSS is applied before init
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    this.createOverlay();
    this.setupAccessibility();
    this.setupEventListeners();
    this.setupStyles();
  }

  createOverlay() {
    // Create overlay backdrop
    this.overlay = document.createElement('div');
    this.overlay.className = 'cart-drawer__overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.insertBefore(this.overlay, this.content);
  }

  setupAccessibility() {
    // Set ARIA attributes for accessibility
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-hidden', 'true');
    this.content.setAttribute('role', 'document');
  }

  setupEventListeners() {
    // Escape key to close
    document.addEventListener('keydown', this.handleEscapeKey);
    
    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }
  }

  setupStyles() {
    // Add position class for CSS targeting
    this.classList.add(`cart-drawer--${this.position}`);
    
    // Ensure content starts in correct position immediately
    if (this.content) {
      if (this.position === 'right') {
        this.content.style.transform = 'translateX(100%)';
      } else {
        this.content.style.transform = 'translateX(-100%)';
      }
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  handleOverlayClick(e) {
    if (e.target === this.overlay) {
      this.close();
    }
  }

  handleCloseClick(e) {
    e.preventDefault();
    this.close();
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    CartDrawer.openInstances = (CartDrawer.openInstances || 0) + 1;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    
    if (this.overlay) {
      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
    }

    // Ensure transition is enabled and remove any inline transform
    if (this.content) {
      this.content.style.transition = 'transform 0.6s cubic-bezier(0.42, 0, 0.58, 1)';
      // Use requestAnimationFrame to ensure the transform change happens after the transition is set
      requestAnimationFrame(() => {
        this.content.style.transform = 'translateX(0)';
      });
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('drawer:open', {
      bubbles: true,
      detail: { drawer: this }
    }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    CartDrawer.openInstances = Math.max(0, (CartDrawer.openInstances || 1) - 1);
    
    // Start fading overlay immediately
    if (this.overlay) {
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');
    }
    
    // Ensure transition is enabled before animating
    if (this.content) {
      this.content.style.transition = 'transform 0.6s cubic-bezier(0.42, 0, 0.58, 1)';
      
      // Use requestAnimationFrame to ensure transition is applied before transform change
      requestAnimationFrame(() => {
        // Start closing animation
        if (this.position === 'right') {
          this.content.style.transform = 'translateX(100%)';
        } else {
          this.content.style.transform = 'translateX(-100%)';
        }

        // Wait for animation to complete before hiding
        setTimeout(() => {
          this.setAttribute('aria-hidden', 'true');
          this.classList.remove('is-open');

          this.restoreBodyScroll();

          // Dispatch custom event
          this.dispatchEvent(new CustomEvent('drawer:close', {
            bubbles: true,
            detail: { drawer: this }
          }));
        }, this.animationDuration);
      });
    } else {
      // Fallback if content is missing
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-open');
      this.restoreBodyScroll();
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  restoreBodyScroll() {
    if ((CartDrawer.openInstances || 0) === 0 && !document.body.classList.contains('mobile-menu-open')) {
      document.body.style.overflow = '';
    }
  }
 
  disconnectedCallback() {

    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscapeKey);
    
    // Restore body scroll if drawer was open
    if (this.isOpen) {
      document.body.style.overflow = '';
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/**
 * Cart Management Functions
 * Handles cart updates, quantity changes, and item removal
 */

// Initialize cart functionality when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initCartHandlers();
});

// Use event delegation on document to handle dynamically added elements
let handlersInitialized = false;

/**
 * Initialize cart event handlers (only once)
 */
function initCartHandlers() {
  if (handlersInitialized) return;
  handlersInitialized = true;

  // Use event delegation on document to handle clicks
  document.addEventListener('click', async (e) => {
    // Quantity increase buttons
    if (e.target.closest('[data-increase-quantity]')) {
      e.preventDefault();
      const button = e.target.closest('[data-increase-quantity]');
      const itemKey = button.getAttribute('data-item-key');
      await updateCartQuantity(itemKey, 'increase');
    }

    // Quantity decrease buttons
    if (e.target.closest('[data-decrease-quantity]')) {
      e.preventDefault();
      const button = e.target.closest('[data-decrease-quantity]');
      const itemKey = button.getAttribute('data-item-key');
      await updateCartQuantity(itemKey, 'decrease');
    }

    // Remove item buttons
    if (e.target.closest('[data-remove-item]')) {
      e.preventDefault();
      const button = e.target.closest('[data-remove-item]');
      const itemKey = button.getAttribute('data-item-key');
      await removeCartItem(itemKey);
    }
  });
}

/**
 * Update cart item quantity
 * @param {string} itemKey - The cart item key
 * @param {string} action - 'increase' or 'decrease'
 */
async function updateCartQuantity(itemKey, action) {
  try {
    // Get current quantity from display
    const quantityDisplay = document.querySelector(`[data-quantity-display="${itemKey}"]`);
    let currentQuantity = quantityDisplay ? parseInt(quantityDisplay.textContent.trim()) : 1;

    // Calculate new quantity
    let newQuantity = currentQuantity;
    if (action === 'increase') {
      newQuantity += 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, newQuantity - 1);
    }

    // Update display immediately (optimistic update)
    if (quantityDisplay) {
      quantityDisplay.textContent = newQuantity;
    }

    // If quantity is 0, remove the item
    if (newQuantity === 0) {
      await removeCartItem(itemKey);
      return;
    }

    // Update cart using change.js endpoint with bundled section rendering
    const updateResponse = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: newQuantity,
        sections: 'cart-drawer-content'
      })
    });

    if (!updateResponse.ok) {
      // Revert optimistic update on error
      if (quantityDisplay) {
        quantityDisplay.textContent = currentQuantity;
      }
      throw new Error('Failed to update cart');
    }

    const responseData = await updateResponse.json();
    
    // Fetch full cart object to ensure we have item_count
    const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
    const cart = await cartRes.json();
    
    // Update cart drawer with bundled section content if available
    if (responseData.sections && responseData.sections['cart-drawer-content']) {
      await updateCartDrawerContent(responseData.sections['cart-drawer-content'], cart);
    } else {
      await refreshCartDrawer(cart);
    }
  } catch (error) {
    console.error('Error updating cart quantity:', error);
    // Reload page on error to ensure consistency
    window.location.reload();
  }
}

/**
 * Remove item from cart
 * @param {string} itemKey - The cart item key
 */
async function removeCartItem(itemKey) {
  try {
    // Remove item by setting quantity to 0 with bundled section rendering
    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0,
        sections: 'cart-drawer-content'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to remove item from cart');
    }

    const responseData = await response.json();
    
    // Fetch full cart object to ensure we have item_count
    const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
    const cart = await cartRes.json();
    
    // Update cart drawer with bundled section content if available
    if (responseData.sections && responseData.sections['cart-drawer-content']) {
      await updateCartDrawerContent(responseData.sections['cart-drawer-content'], cart);
    } else {
      await refreshCartDrawer(cart);
    }
  } catch (error) {
    console.error('Error removing cart item:', error);
  }
}

/**
 * Update cart drawer content with HTML from section rendering
 * @param {string} html - The HTML content from section rendering
 * @param {Object} cart - The updated cart object
 */
async function updateCartDrawerContent(html, cart) {
  try {
    const cartDrawerBody = document.getElementById('cart-drawer-body');
    if (!cartDrawerBody) {
      console.error('Cart drawer body not found');
      return;
    }

    // Parse the HTML - section rendering wraps content in a div with section ID
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find the cart drawer body content inside the section wrapper
    // The section file wraps the snippet in #cart-drawer-body
    const sectionWrapper = doc.querySelector('#cart-drawer-body');
    
    if (sectionWrapper) {
      // Extract the entire snippet content (items + footer)
      cartDrawerBody.innerHTML = sectionWrapper.innerHTML;
    } else {
      // Fallback: try to find items and footer separately
      const items = doc.querySelector('.cart-drawer__items');
      const footer = doc.querySelector('.cart-drawer__footer');
      const empty = doc.querySelector('.cart-drawer__empty');
      
      if (items || footer || empty) {
        cartDrawerBody.innerHTML = (items?.outerHTML || '') + 
                                   (footer?.outerHTML || '') + 
                                   (empty?.outerHTML || '');
      } else {
        // Last resort: use body content
        cartDrawerBody.innerHTML = doc.body.innerHTML;
      }
    }

    // Update cart total
    updateCartTotal(cart);

    // Update cart count in header if it exists
    updateCartCount(cart.item_count);

    // Dispatch cart update event
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart }
    }));
  } catch (error) {
    console.error('Error updating cart drawer content:', error);
    await refreshCartDrawer(cart);
  }
}

/**
 * Refresh cart drawer content
 * @param {Object} cart - The updated cart object (optional)
 */
async function refreshCartDrawer(cart = null) {
  try {
    // If cart is not provided, fetch it
    if (!cart) {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      cart = await response.json();
    }

    // Get the cart drawer body element
    const cartDrawerBody = document.getElementById('cart-drawer-body');
    if (!cartDrawerBody) {
      console.error('Cart drawer body not found');
      return;
    }

    // Use Section Rendering API to fetch the section
    const url = new URL(window.location.origin + window.Shopify.routes.root);
    url.searchParams.set('section_id', 'cart-drawer-content');
    
    const sectionResponse = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (sectionResponse.ok) {
      const data = await sectionResponse.json();
      // Section rendering returns sections object with section ID as key
      const html = data.sections?.['cart-drawer-content'] || data.html || '';
      
      if (html) {
        await updateCartDrawerContent(html, cart);
        return;
      }
    }

    // Fallback: reload the page if section rendering fails
    window.location.reload();
  } catch (error) {
    console.error('Error refreshing cart drawer:', error);
    // Fallback: reload the page
    window.location.reload();
  }
}

/**
 * Update cart total display
 * @param {Object} cart - The cart object
 */
function updateCartTotal(cart) {
  const totalElement = document.querySelector('.cart-drawer__total-price');
  if (totalElement && cart.total_price !== undefined) {
    // Format price (assuming Shopify money format)
    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cart.currency || 'USD',
      minimumFractionDigits: 2
    }).format(cart.total_price / 100);

    totalElement.textContent = formattedPrice;
  }
}

/**
 * Update cart count in header and all cart count displays
 * @param {number} count - The item count
 */
function updateCartCount(count) {
  // Update elements with data-cart-count attribute
  const cartCountElements = document.querySelectorAll('[data-cart-count]');
  cartCountElements.forEach(element => {
    element.textContent = count;
  });
  
  // Update cart header button text in drawer
  document.querySelectorAll('[data-cart-header]').forEach(el => {
    el.textContent = `Cart (${count})`;
  });
  
  // Update header cart count spans (desktop and mobile)
  // These are the actual elements displaying cart count in the header
  document.querySelectorAll('.header__icons span, .header__mobile-cart span').forEach(el => {
    el.textContent = count;
  });
}

